import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { demoStages, demoTripDistanceKm } from '../data/demoTrip.js';
import { RouteGeometryPreview } from './RouteGeometryPreview.js';

const DemoRouteMap = lazy(() =>
  import('./DemoRouteMap.js').then((module) => ({ default: module.DemoRouteMap })),
);

export function DemoMap({
  compact = false,
  selectedStageId,
  label = 'REAL ROUTE DEMO',
  load = 'viewport',
}: {
  compact?: boolean;
  selectedStageId?: string;
  label?: string;
  load?: 'viewport' | 'interaction';
}) {
  const container = useRef<HTMLElement>(null);
  const [mapReady, setMapReady] = useState(false);
  const selectedStage =
    demoStages.find((stage) => stage.id === selectedStageId) ?? demoStages.at(-1)!;
  useEffect(() => {
    if (load === 'interaction' || mapReady || !container.current) return;
    if (!('IntersectionObserver' in window)) {
      setMapReady(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setMapReady(true);
          observer.disconnect();
        }
      },
      { rootMargin: '220px' },
    );
    observer.observe(container.current);
    return () => observer.disconnect();
  }, [load, mapReady]);
  return (
    <figure className={`demo-map${compact ? ' demo-map--compact' : ''}`} ref={container}>
      <div className="map-label">{label}</div>
      {mapReady ? (
        <Suspense fallback={<div className="demo-route-map-loading">Loading the route map…</div>}>
          <DemoRouteMap selectedStageId={selectedStageId} />
        </Suspense>
      ) : (
        <div className="demo-route-map-placeholder">
          <RouteGeometryPreview
            segments={demoStages}
            ariaLabel="Production-generated Via Claudia bicycle route from Augsburg to Reschen Pass"
          />
          {load === 'interaction' ? (
            <button
              type="button"
              className="button button--small"
              onClick={() => setMapReady(true)}
            >
              Load interactive map
            </button>
          ) : (
            <span>Interactive route map</span>
          )}
        </div>
      )}
      <figcaption>
        <div>
          <span className={`status-chip${selectedStage.state === 'live' ? '' : ' is-complete'}`}>
            <i /> {selectedStage.state === 'live' ? 'Live' : 'Complete'}
          </span>
          <strong>
            Stage {selectedStage.number} · {selectedStage.title}
          </strong>
          <span>
            {selectedStage.from} → {selectedStage.to}
          </span>
        </div>
        <dl>
          <div>
            <dt>This day</dt>
            <dd>{selectedStage.distanceKm.toFixed(1)} km</dd>
          </div>
          <div>
            <dt>Whole trip</dt>
            <dd>{demoTripDistanceKm.toFixed(1)} km</dd>
          </div>
        </dl>
      </figcaption>
      {!compact && (
        <div className="demo-stages" aria-label="Demo trip stages">
          {demoStages.map((stage) => (
            <div className={stage.state === 'live' ? 'is-live' : ''} key={stage.number}>
              <span>{stage.number}</span>
              <p>
                <strong>{stage.to}</strong>
                <small>{stage.distanceKm.toFixed(1)} km</small>
              </p>
            </div>
          ))}
        </div>
      )}
    </figure>
  );
}
